import React, { useEffect, useMemo, useRef, useState } from "react";
import Highcharts from "highcharts/highcharts";
import HighchartsReact from "highcharts-react-official";

import exportingModule from "highcharts/modules/exporting";
import offlineExportingModule from "highcharts/modules/offline-exporting";
import exportDataModule from "highcharts/modules/export-data";

import LoadingBlock from "../LoadingBlock/LoadingBlock";
import { baseChartOptions } from "./charts.config";

function initModule(mod, H) {
  const fn = mod?.default || mod;
  if (typeof fn === "function") fn(H);
}

initModule(exportingModule, Highcharts);
initModule(offlineExportingModule, Highcharts);
initModule(exportDataModule, Highcharts);

export default function ReleasePerDayLineChart({
  categories = [],
  values = [],
  loading = false,
  title = "Releases por dia",
  filename = "releases-por-dia",
  loadingLabel = "Carregando releases por dia.",
  exportMode = false,
}) {
  const chartRef = useRef(null);

  const PURPLE = "#6D28D9";
  const PURPLE_FILL = "rgba(109, 40, 217, 0.18)";
  const TITLE_COLOR = "#614a9e";

  const CARD_MIN_H = 360;
  const CHART_H = 320;

  const [chartKey, setChartKey] = useState(0);

  useEffect(() => {
    const onEnd = () => setChartKey((k) => k + 1);
    window.addEventListener("dashboard-export:end", onEnd);
    return () => window.removeEventListener("dashboard-export:end", onEnd);
  }, []);

  const normalizedValues = useMemo(
    () => (values || []).map((v) => (Number.isFinite(+v) ? +v : 0)),
    [values]
  );

  const wrapStyle = useMemo(() => {
    if (!exportMode) {
      return {
        position: "relative",
        borderRadius: 12,
        padding: 12,
        border: "none",
        background: "var(--background-graphic)",
        boxShadow: "0 8px 20px rgba(15, 23, 42, 0.05)",
        overflow: "hidden",
        minHeight: CARD_MIN_H,
      };
    }

    return {
      position: "relative",
      borderRadius: 0,
      padding: 0,
      border: "none",
      background: "var(--background-graphic)",
      boxShadow: "none",
      overflow: "hidden",
      minHeight: 560,
      width: "100%",
    };
  }, [exportMode]);

  const options = useMemo(() => {
    return {
      ...baseChartOptions,

      chart: {
        type: "areaspline",
        height: exportMode ? 560 : CHART_H,
        spacing: exportMode ? [0, 0, 0, 0] : [24, 16, 16, 24],
        backgroundColor: "transparent",
        marginTop: exportMode ? 0 : 64,
        animation: !exportMode,
      },

      title: {
        text: exportMode ? null : title,
        align: "left",
        x: 0,
        y: -3,
        margin: 12,
        style: { fontSize: "14px", fontWeight: "800", color: TITLE_COLOR },
      },

      exporting: { enabled: false },

      xAxis: {
        categories,
        tickLength: 0,
        lineWidth: 0,
        labels: {
          style: {
            fontSize: exportMode ? "13px" : "12px",
            fontWeight: "700",
            color: "var(--text-color)",
          },
        },
      },

      yAxis: {
        title: { text: null },
        allowDecimals: false,
        gridLineDashStyle: "ShortDot",
        gridLineColor: "rgba(17, 24, 39, 0.10)",
        labels: {
          style: {
            fontSize: exportMode ? "13px" : "11px",
            fontWeight: "700",
            color: "var(--text-color)",
          },
        },
      },

      tooltip: {
        useHTML: true,
        formatter: function () {
          return `<b>${this.x}</b><br/>Releases: <b>${this.y}</b>`;
        },
      },

      legend: { enabled: false },

      plotOptions: {
        series: {
          animation: !exportMode,
          marker: {
            enabled: true,
            radius: exportMode ? 4 : 3,
            lineWidth: 2,
            lineColor: PURPLE,
            fillColor: "#fff",
          },
        },
        areaspline: {
          lineWidth: 3,
          color: PURPLE,
          fillColor: PURPLE_FILL,
        },
      },

      series: [{ name: "Releases", data: normalizedValues }],

      credits: { enabled: false },
    };
  }, [categories, normalizedValues, title, exportMode]);

  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart) return;

    const t = setTimeout(() => {
      try {
        chart.reflow();

        if (exportMode) {
          const w = chart.renderTo?.clientWidth || null;
          chart.setSize(w, 560, false);
          chart.reflow();
        }
      } catch {}
    }, exportMode ? 60 : 0);

    return () => clearTimeout(t);
  }, [exportMode, categories, normalizedValues]);

  const handleExportPNG = () => {
    const chart = chartRef.current?.chart;
    if (!chart) return;

    chart.exportChart(
      { type: "image/png", filename },
      { chart: { backgroundColor: "var(--background-graphic)" } }
    );
  };

  const exportBtnStyle = {
    position: "absolute",
    top: 10,
    right: 10,
    zIndex: 5,
    height: 34,
    padding: "0 12px",
    borderRadius: 12,
    border: "none",
    background: "var(--bottom-dropdown-color)",
    color: "var(--text-color)",
    fontWeight: 900,
    fontSize: 12,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    cursor: "pointer",
    userSelect: "none",
    transition: "transform .08s ease",
  };

  const exportBtnHoverStyle = {
    boxShadow: "0 6px 14px rgba(97, 74, 158, 0.15)",
    backgroundColor: "#614a9e",
    color: "white",
  };

  const iconStyle = {
    width: 18,
    height: 18,
    display: "grid",
    placeItems: "center",
    borderRadius: 8,
    background:
      "linear-gradient(135deg, var(--icon-gradient-start), var(--icon-gradient-end))",
    color: "var(--color-text-button-action)",
    fontSize: 12,
    lineHeight: 1,
    flex: "0 0 auto",
  };

  return (
    <LoadingBlock loading={loading} label={loadingLabel}>
      <div style={wrapStyle}>
        {!exportMode ? (
          <ExportButton
            onClick={handleExportPNG}
            style={exportBtnStyle}
            hoverStyle={exportBtnHoverStyle}
            iconStyle={iconStyle}
          />
        ) : null}

        <HighchartsReact
          key={chartKey}
          ref={chartRef}
          highcharts={Highcharts}
          options={options}
          containerProps={{ style: { height: exportMode ? 560 : CHART_H } }}
        />
      </div>
    </LoadingBlock>
  );
}

function ExportButton({ onClick, style, hoverStyle, iconStyle }) {
  const [hover, setHover] = useState(false);

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ ...style, ...(hover ? hoverStyle : {}) }}
      title="Exportar PNG"
    >
      <span style={iconStyle}>⭳</span>
      Exportar PNG
    </button>
  );
}
